import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Droplets, Zap, Bell, HeartHandshake, BadgeCheck, HeartPulse, Tent,
  ShieldCheck, Award, ChevronDown, UserPlus, Building2, Search, Clock,
} from 'lucide-react';
import { Card, Badge, Reveal, useIsMobile } from '../components/ui';
import PageLayout from '../components/layout/PageLayout';
import {
  DropletArt, MatchRadarArt, AlertArt, CampArt, ShieldArt, CertificateArt,
  CooldownArt, HandoffArt,
} from '../components/illustrations';

/* ─────────────────────────────────────────────────────────────
   Everything on this page mirrors real platform behaviour — the
   numbers below come from the backend, not from marketing copy:
     · 15 km default match radius   → core/config.py DEFAULT_SEARCH_RADIUS_KM
     · 12 screening questions,
       18–65 years, ≥50 kg,
       56 / 7 / 28-day cooldowns    → services/eligibility.py
     · badge tiers 1 / 5 / 10 / 25  → services/certificates.py BADGE_TIERS
     · compatibility tables         → services/matching.py
   If any of those change, this page has to change with them.
   ───────────────────────────────────────────────────────────── */

const ALL_TYPES = ['O-', 'O+', 'A-', 'A+', 'B-', 'B+', 'AB-', 'AB+'];

// Whole blood / red cells — who can donate TO each recipient.
const WHOLE_BLOOD_DONORS = {
  'A+': ['A+', 'A-', 'O+', 'O-'],
  'A-': ['A-', 'O-'],
  'B+': ['B+', 'B-', 'O+', 'O-'],
  'B-': ['B-', 'O-'],
  'AB+': ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'],
  'AB-': ['A-', 'B-', 'AB-', 'O-'],
  'O+': ['O+', 'O-'],
  'O-': ['O-'],
};

// Plasma is the mirror image, and Rh doesn't apply — only the ABO letter does.
const PLASMA_ABO = { O: ['O', 'A', 'B', 'AB'], A: ['A', 'AB'], B: ['B', 'AB'], AB: ['AB'] };
const abo = (t) => t.replace(/[+-]$/, '');
const plasmaDonors = (recipient) =>
  ALL_TYPES.filter(t => PLASMA_ABO[abo(recipient)].includes(abo(t)));

const PATHS = [
  {
    to: '#donating', Icon: Droplets, title: 'I want to donate',
    desc: 'Screening, eligibility, and what happens after you give.',
  },
  {
    to: '#requesting', Icon: HeartPulse, title: 'I need blood',
    desc: 'Post a request and reach matching donors near the hospital.',
  },
  {
    to: '#organizations', Icon: Building2, title: "We're an organization",
    desc: 'Get verified, run donation camps, record who donated.',
  },
];

const REQUEST_STEPS = [
  {
    Icon: HeartPulse,
    title: 'A request is posted',
    body: 'Blood type, component, units needed, hospital, and how urgent it is — critical, high, or normal. Posting needs a verified email and phone number, so every request on the platform traces back to a real, reachable person.',
  },
  {
    Icon: Zap,
    title: 'Matching donors are found',
    body: 'We look for donors whose blood type is compatible with the request, who are within 15 km of the hospital, who are currently eligible to donate, and who have opted into the component being asked for. Nobody has to be watching a feed for this to happen.',
    art: <MatchRadarArt />,
  },
  {
    Icon: Bell,
    title: 'Those donors are alerted',
    body: 'Each matched donor gets an SMS and an email with the details, plus an in-app notification. Alerts go out in the background the moment the request is created — nothing waits on a person to press send.',
    art: <AlertArt />,
  },
  {
    Icon: HeartHandshake,
    title: 'A donor responds',
    body: "A donor who can help accepts the request — and only then is their name and phone number shared with the requester. Until someone accepts, no contact details move in either direction. The requester confirms the donor they're going with.",
    art: <HandoffArt />,
  },
  {
    Icon: BadgeCheck,
    title: 'The donation is confirmed',
    body: 'After donating, the donor marks it done and the requester confirms it happened. That confirmation is what makes a donation record trustworthy instead of self-declared — it logs the donation, issues the certificate, and starts the recovery period.',
  },
];

const ELIGIBILITY = [
  { label: 'Age', value: '18 – 65 years' },
  { label: 'Weight', value: '50 kg or more' },
  { label: 'Health screening', value: '12 questions' },
  { label: 'Since last donation', value: '56 days (whole blood)' },
];

const COOLDOWNS = [
  { label: 'Whole blood', days: 56, note: 'The standard walk-in donation' },
  { label: 'Platelets', days: 7, note: 'Apheresis — by appointment' },
  { label: 'Plasma', days: 28, note: 'Apheresis — by appointment' },
  { label: 'Cryoprecipitate', days: 28, note: 'Apheresis — by appointment' },
];

const CAMP_STEPS = [
  { title: 'Register your organization', body: 'Hospital, NGO, blood bank, or corporate. Add your name, type, registration number, and address.' },
  { title: 'Get verified by an admin', body: "A human reviews your organization before it can list anything. Until then you can explore your dashboard, but camp creation stays locked — that's what stops anyone from advertising a camp that doesn't exist." },
  { title: 'Create a camp with a real location', body: "Venue, address, date, and slots. We pin it on the map from the address, or you can pin the exact spot on site — donors get a map and one-tap directions either way." },
  { title: 'Donors register for a slot', body: 'Only eligible donors can book, and slots are capped. You see every registrant with their blood type and contact details, so you can plan the day.' },
  { title: 'Record who actually donated', body: "On the day, mark each donor who gave. That counts their donation, issues their certificate, and starts their recovery period — and if they'd booked another camp inside that window, their slot there is released automatically for someone who can use it." },
];

const BADGES = [
  { tier: 'Bronze', at: 1, color: '#B45309' },
  { tier: 'Silver', at: 5, color: '#6B7280' },
  { tier: 'Gold', at: 10, color: '#D97706' },
  { tier: 'Platinum', at: 25, color: '#0F766E' },
];

const PRIVACY_POINTS = [
  'Logged-out visitors see a deliberately redacted request: city only — no patient name, no hospital name, no address, no GPS. This platform is not a public directory of who is in which hospital.',
  'A donor’s name and phone number reach a requester only after that donor accepts. Not on match, not on alert — on accept.',
  'Posting or accepting a request requires a verified email and phone number.',
  'Organizations are reviewed by an admin before they can list a camp.',
  'Passwords are hashed, never stored in plain text, and sessions use short-lived tokens rather than a permanent credential in your browser.',
];

const FAQS = [
  {
    q: 'Does it cost anything?',
    a: 'No. Donors, recipients, and organizations all use the platform for free.',
  },
  {
    q: 'Can one account both donate and request?',
    a: 'Yes. A recipient account can become a donor account at any time from the home page or your account settings — you keep the same login, and you can still post requests afterwards.',
  },
  {
    q: 'Do I have to share my exact location?',
    a: "No. GPS is optional — you can enter just a city and state instead. Sharing coordinates only makes distance-based matching more accurate; without it you're still matched by city.",
  },
  {
    q: 'What if I’m not eligible right now?',
    a: "You'll see exactly why, and when it lifts. A recovery period after donating clears itself once the days are up. A deferral from a health answer (a recent tattoo, surgery, or illness) needs you to re-answer the screening once that period has passed.",
  },
  {
    q: 'Why does the requester have to confirm my donation?',
    a: 'Because a certificate anyone could issue themselves would be worthless. The person who received the blood is the one who confirms it happened — that is what makes it count toward your record, instead of your own say-so.',
  },
  {
    q: 'Is this a medical service?',
    a: 'No. This platform coordinates people; it does not provide medical care or run blood tests. Hospitals re-verify compatibility before any actual transfusion. In an emergency, contact your local emergency services first.',
  },
];

const CTAS = [
  { to: '/register?role=donor', Icon: UserPlus, label: 'Become a donor', primary: true },
  { to: '/requests', Icon: Search, label: 'Browse open requests' },
  { to: '/camps', Icon: Tent, label: 'Find a camp' },
];

const ctaBase = {
  display: 'inline-flex', alignItems: 'center', gap: '7px',
  padding: '11px 22px', borderRadius: '9px', border: 'none',
  fontFamily: 'var(--font)', fontSize: '13.5px', fontWeight: 700,
  letterSpacing: '-0.01em', cursor: 'pointer',
};
const ctaPrimary = {
  ...ctaBase, background: 'white', color: 'var(--red)',
  boxShadow: '0 4px 14px rgba(0,0,0,0.16)',
};
const ctaSecondary = {
  ...ctaBase, background: 'rgba(255,255,255,0.16)', color: 'white',
  border: '1.5px solid rgba(255,255,255,0.35)',
};

const S = {
  h2: { fontSize: '20px', fontWeight: 800, letterSpacing: '-0.03em', color: 'var(--gray-900)' },
  eyebrow: {
    fontSize: '11px', fontWeight: 700, color: 'var(--red)',
    textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '6px',
  },
  lead: { fontSize: '13.5px', color: 'var(--gray-500)', lineHeight: 1.65, marginTop: '6px' },
  body: { fontSize: '13.5px', color: 'var(--gray-600)', lineHeight: 1.7 },
};

function SectionHeading({ eyebrow, title, lead, id }) {
  return (
    <div id={id} style={{ scrollMarginTop: '90px', marginBottom: '14px' }}>
      <div style={S.eyebrow}>{eyebrow}</div>
      <h2 style={S.h2}>{title}</h2>
      {lead && <p style={{ ...S.lead, maxWidth: '640px' }}>{lead}</p>}
    </div>
  );
}

function Faq({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: '1px solid var(--gray-100)' }}>
      <button
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: '12px', width: '100%', background: 'none', padding: '14px 0',
          textAlign: 'left', fontSize: '13.5px', fontWeight: 600, color: 'var(--gray-900)',
        }}
      >
        {q}
        <ChevronDown
          size={16}
          color="var(--gray-400)"
          style={{ flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform var(--t-slow)' }}
        />
      </button>
      {open && (
        <p style={{ ...S.body, paddingBottom: '14px', animation: 'bb-fadeUp 0.3s ease both' }}>{a}</p>
      )}
    </div>
  );
}

export default function HowItWorks() {
  const isMobile = useIsMobile();
  const [recipient, setRecipient] = useState('O+');
  const [component, setComponent] = useState('whole_blood');

  const donors = component === 'plasma' ? plasmaDonors(recipient) : WHOLE_BLOOD_DONORS[recipient];

  return (
    <PageLayout>
      <div style={{ maxWidth: '880px', margin: '0 auto' }}>

        {/* ── Hero ── */}
        <div style={{
          background: 'linear-gradient(140deg, var(--red-deeper) 0%, var(--red-dark) 45%, var(--red) 100%)',
          borderRadius: 'var(--radius-lg)',
          padding: isMobile ? '30px 22px' : '46px 44px',
          color: 'white', marginBottom: isMobile ? '18px' : '26px',
          position: 'relative', overflow: 'hidden',
          animation: 'bb-fadeUp 0.7s ease both',
        }}>
          <div style={{ position: 'absolute', right: '-80px', top: '-80px', width: '300px', height: '300px', borderRadius: '50%', background: 'rgba(255,255,255,0.06)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', right: '60px', bottom: '-110px', width: '220px', height: '220px', borderRadius: '50%', background: 'rgba(255,255,255,0.05)', pointerEvents: 'none' }} />

          <div style={{ display: 'flex', alignItems: 'center', gap: '32px', position: 'relative', zIndex: 1 }}>
            <div style={{ flex: 1 }}>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: '7px',
                background: 'rgba(255,255,255,0.14)', borderRadius: '20px',
                padding: '5px 13px', marginBottom: '16px',
                border: '1px solid rgba(255,255,255,0.2)',
              }}>
                <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: 'rgba(255,255,255,0.9)', animation: 'bb-pulse 2s ease-in-out infinite' }} />
                <span style={{ fontSize: '11px', fontWeight: 600, color: 'rgba(255,255,255,0.9)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                  The whole picture
                </span>
              </div>

              <h1 style={{ fontSize: isMobile ? '28px' : '38px', fontWeight: 900, lineHeight: 1.1, letterSpacing: '-0.04em', marginBottom: '14px' }}>
                How HopeForSomeone works
              </h1>
              <p style={{ fontSize: isMobile ? '14px' : '15px', opacity: 0.85, lineHeight: 1.65, maxWidth: '460px' }}>
                A request goes up, the right donors nearby hear about it in seconds, and someone
                shows up. Here's every step of that — plus how eligibility, camps, certificates,
                and your privacy actually work.
              </p>
            </div>

            {!isMobile && (
              <div style={{ animation: 'bb-float 5s ease-in-out infinite', opacity: 0.95 }}>
                <DropletArt size={150} onDark />
              </div>
            )}
          </div>
        </div>

        {/* ── Pick a path ── */}
        <div style={{
          display: 'grid', gap: '10px',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
          marginBottom: isMobile ? '26px' : '38px',
        }}>
          {PATHS.map(({ to, Icon, title, desc }, i) => (
            <Reveal key={title} delay={i * 80}>
              <a href={to} style={{ display: 'block', color: 'inherit' }}>
                <Card interactive style={{ height: '100%' }}>
                  <div style={{
                    width: '38px', height: '38px', borderRadius: '10px', marginBottom: '12px',
                    background: 'var(--red-light)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Icon size={18} color="var(--red-dark)" strokeWidth={2} />
                  </div>
                  <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--gray-900)', marginBottom: '4px' }}>{title}</div>
                  <div style={{ fontSize: '12.5px', color: 'var(--gray-500)', lineHeight: 1.55 }}>{desc}</div>
                </Card>
              </a>
            </Reveal>
          ))}
        </div>

        {/* ── The core flow ── */}
        <SectionHeading
          id="requesting"
          eyebrow="The core flow"
          title="From an urgent request to a donation"
          lead="This is the path every blood request takes. Steps 2 and 3 happen on their own, in the background, within seconds of a request going up."
        />

        <div style={{ marginBottom: isMobile ? '30px' : '44px' }}>
          {REQUEST_STEPS.map(({ Icon, title, body, art }, i) => (
            <Reveal key={title} delay={i * 60}>
              <div style={{ display: 'flex', gap: isMobile ? '14px' : '18px' }}>
                {/* Timeline rail */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                  <div style={{
                    width: '38px', height: '38px', borderRadius: '50%', flexShrink: 0,
                    background: 'var(--red)', color: 'white',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '14px', fontWeight: 800, boxShadow: 'var(--shadow-red)',
                  }}>
                    {i + 1}
                  </div>
                  {i < REQUEST_STEPS.length - 1 && (
                    <div style={{ width: '2px', flex: 1, minHeight: '28px', background: 'linear-gradient(var(--red-mid), var(--gray-200))' }} />
                  )}
                </div>

                <div style={{ flex: 1, paddingBottom: i < REQUEST_STEPS.length - 1 ? '22px' : 0, minWidth: 0 }}>
                  <div style={{
                    display: 'flex', gap: isMobile ? '14px' : '22px',
                    alignItems: 'flex-start',
                    flexDirection: isMobile ? 'column' : 'row',
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                        <Icon size={15} color="var(--red)" strokeWidth={2.2} />
                        <span style={{ fontSize: '15px', fontWeight: 700, color: 'var(--gray-900)' }}>{title}</span>
                      </div>
                      <p style={S.body}>{body}</p>
                    </div>
                    {art && (
                      <div style={{ flexShrink: 0, alignSelf: isMobile ? 'center' : 'flex-start' }}>
                        {art}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        {/* ── Donating / eligibility ── */}
        <SectionHeading
          id="donating"
          eyebrow="For donors"
          title="Whether you can donate, and when you can again"
          lead="Eligibility isn't a guess — it's a screening you fill in once, re-evaluated against the same rules a blood bank uses."
        />

        <Reveal>
          <Card style={{ marginBottom: '12px' }}>
            <div style={{
              display: 'grid', gap: isMobile ? '14px' : '18px',
              gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
              marginBottom: '18px',
            }}>
              {ELIGIBILITY.map(({ label, value }) => (
                <div key={label}>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>
                    {label}
                  </div>
                  <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--gray-900)' }}>{value}</div>
                </div>
              ))}
            </div>

            <p style={S.body}>
              The screening asks 12 yes/no questions — things like recent illness, a recent tattoo or
              surgery, medication in the last few days, and permanent conditions. Some answers defer you
              for a set period; a few rule out donation for good. You always see the specific reason and,
              when it's temporary, the date it lifts.
            </p>
          </Card>
        </Reveal>

        <Reveal>
          <Card style={{ marginBottom: isMobile ? '30px' : '44px' }}>
            <div style={{ display: 'flex', gap: isMobile ? '18px' : '28px', alignItems: 'center', flexDirection: isMobile ? 'column' : 'row' }}>
              <div style={{ flexShrink: 0 }}>
                <CooldownArt size={128} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <Clock size={15} color="var(--red)" strokeWidth={2.2} />
                  <span style={{ fontSize: '15px', fontWeight: 700, color: 'var(--gray-900)' }}>Your recovery period</span>
                </div>
                <p style={{ ...S.body, marginBottom: '14px' }}>
                  Once a donation is recorded, you're paused until your body has replaced what you gave.
                  How long depends on what you donated. When the time is up, your eligibility comes back
                  on its own — you don't have to do anything.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {COOLDOWNS.map(({ label, days, note }) => (
                    <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      {/* Fixed, non-wrapping first column — "56 days" breaks
                          across two lines at phone widths otherwise. */}
                      <div style={{ minWidth: '74px', whiteSpace: 'nowrap' }}>
                        <Badge color={days === 56 ? 'red' : 'blue'}>{days} days</Badge>
                      </div>
                      <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--gray-800)', whiteSpace: 'nowrap' }}>{label}</span>
                      <span style={{ fontSize: '12px', color: 'var(--gray-400)', minWidth: 0 }}>{note}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Card>
        </Reveal>

        {/* ── Compatibility explorer ── */}
        <SectionHeading
          eyebrow="Matching"
          title="Who can donate to whom"
          lead="Pick the patient's blood type to see which donors we'd alert. This is the same table the matching engine uses."
        />

        <Reveal>
          <Card style={{ marginBottom: isMobile ? '30px' : '44px' }}>
            {/* Component switch */}
            <div style={{ display: 'inline-flex', gap: '4px', background: 'var(--gray-100)', padding: '4px', borderRadius: '9px', marginBottom: '18px' }}>
              {[['whole_blood', 'Whole blood / red cells'], ['plasma', 'Plasma']].map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setComponent(key)}
                  style={{
                    padding: '6px 14px', borderRadius: '7px', fontSize: '12.5px', fontWeight: 600,
                    background: component === key ? 'var(--white)' : 'transparent',
                    color: component === key ? 'var(--gray-900)' : 'var(--gray-500)',
                    boxShadow: component === key ? 'var(--shadow-xs)' : 'none',
                    transition: 'background var(--t), color var(--t)',
                  }}
                >
                  {label}
                </button>
              ))}
            </div>

            <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>
              Patient needs
            </div>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '20px' }}>
              {ALL_TYPES.map(t => (
                <button
                  key={t}
                  onClick={() => setRecipient(t)}
                  style={{
                    minWidth: '48px', padding: '8px 10px', borderRadius: '8px',
                    fontSize: '13px', fontWeight: 800, letterSpacing: '-0.02em',
                    border: `1.5px solid ${recipient === t ? 'var(--red)' : 'var(--gray-200)'}`,
                    background: recipient === t ? 'var(--red)' : 'var(--white)',
                    color: recipient === t ? 'white' : 'var(--gray-700)',
                    boxShadow: recipient === t ? 'var(--shadow-red)' : 'none',
                    transform: recipient === t ? 'translateY(-1px)' : 'none',
                    transition: 'all var(--t)',
                  }}
                >
                  {t}
                </button>
              ))}
            </div>

            <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>
              Donors we'd alert — {donors.length} of 8 types
            </div>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '16px' }}>
              {ALL_TYPES.map((t, i) => {
                const ok = donors.includes(t);
                return (
                  <div
                    key={t}
                    style={{
                      minWidth: '48px', padding: '8px 10px', borderRadius: '8px', textAlign: 'center',
                      fontSize: '13px', fontWeight: 800, letterSpacing: '-0.02em',
                      border: `1.5px solid ${ok ? 'var(--green)' : 'var(--gray-200)'}`,
                      background: ok ? 'var(--green-light)' : 'var(--gray-50)',
                      color: ok ? 'var(--green-dark)' : 'var(--gray-300)',
                      transition: 'all var(--t-slow)',
                      animation: ok ? `bb-fadeUp 0.4s ease ${i * 40}ms both` : undefined,
                    }}
                  >
                    {t}
                  </div>
                );
              })}
            </div>

            <p style={{ fontSize: '12.5px', color: 'var(--gray-500)', lineHeight: 1.6 }}>
              {component === 'plasma'
                ? 'Plasma runs the opposite way to red cells — AB is the universal plasma donor, and O patients can accept plasma from anyone. The +/− (Rh) part doesn’t apply to plasma at all.'
                : 'O− is the universal red-cell donor; AB+ patients can accept from anyone. Platelets and cryoprecipitate are matched using this same table — deliberately more cautious than strictly necessary.'}
              {' '}Distance matters too: by default we alert compatible donors within 15 km of the hospital.
              A hospital always re-verifies compatibility before any transfusion.
            </p>
          </Card>
        </Reveal>

        {/* ── Organizations & camps ── */}
        <SectionHeading
          id="organizations"
          eyebrow="For organizations"
          title="Running a donation camp"
          lead="Hospitals, NGOs, blood banks, and companies can host camps — once a human has checked they're real."
        />

        <Reveal>
          <Card style={{ marginBottom: isMobile ? '30px' : '44px' }}>
            <div style={{ display: 'flex', gap: isMobile ? '18px' : '28px', flexDirection: isMobile ? 'column' : 'row' }}>
              <div style={{ flexShrink: 0, alignSelf: isMobile ? 'center' : 'flex-start' }}>
                <CampArt size={132} />
              </div>
              <ol style={{ flex: 1, minWidth: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {CAMP_STEPS.map(({ title, body }, i) => (
                  <li key={title} style={{ display: 'flex', gap: '11px' }}>
                    <span style={{
                      width: '22px', height: '22px', borderRadius: '50%', flexShrink: 0,
                      background: 'var(--red-light)', color: 'var(--red-dark)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '11.5px', fontWeight: 800, marginTop: '1px',
                    }}>
                      {i + 1}
                    </span>
                    <div>
                      <div style={{ fontSize: '13.5px', fontWeight: 700, color: 'var(--gray-900)', marginBottom: '2px' }}>{title}</div>
                      <p style={{ fontSize: '13px', color: 'var(--gray-600)', lineHeight: 1.65 }}>{body}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </Card>
        </Reveal>

        {/* ── Certificates & badges ── */}
        <SectionHeading
          eyebrow="Recognition"
          title="Certificates and donor badges"
          lead="Every verified donation is recorded against your name — and it's verified precisely so that the record means something."
        />

        <Reveal>
          <Card style={{ marginBottom: isMobile ? '30px' : '44px' }}>
            <div style={{ display: 'flex', gap: isMobile ? '18px' : '28px', alignItems: 'center', flexDirection: isMobile ? 'column' : 'row', marginBottom: '20px' }}>
              <div style={{ flexShrink: 0 }}>
                <CertificateArt size={130} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <Award size={15} color="var(--red)" strokeWidth={2.2} />
                  <span style={{ fontSize: '15px', fontWeight: 700, color: 'var(--gray-900)' }}>A certificate per donation</span>
                </div>
                <p style={S.body}>
                  When a donation is confirmed — by the requester who received it, or by the organization
                  that hosted the camp — you get a numbered certificate you can
                  download as a PDF. It snapshots what you gave and where, so it stays accurate even if
                  the original request or camp record changes later.
                </p>
              </div>
            </div>

            <div style={{
              display: 'grid', gap: '10px',
              gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
            }}>
              {BADGES.map(({ tier, at, color }, i) => (
                <Reveal key={tier} delay={i * 70}>
                  <div style={{
                    border: '1px solid var(--gray-200)', borderTop: `3px solid ${color}`,
                    borderRadius: 'var(--radius)', padding: '14px', textAlign: 'center',
                    background: 'var(--white)', boxShadow: 'var(--shadow-xs)',
                  }}>
                    <div style={{ fontSize: '14px', fontWeight: 800, color, letterSpacing: '-0.02em' }}>{tier}</div>
                    <div style={{ fontSize: '11.5px', color: 'var(--gray-500)', marginTop: '3px' }}>
                      {at} donation{at > 1 ? 's' : ''}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </Card>
        </Reveal>

        {/* ── Privacy & safety ── */}
        <SectionHeading
          eyebrow="Trust"
          title="What we protect, and how"
          lead="A platform that knows blood types, phone numbers, and hospital locations has to be careful with all three."
        />

        <Reveal>
          <Card style={{ marginBottom: isMobile ? '30px' : '44px' }}>
            <div style={{ display: 'flex', gap: isMobile ? '18px' : '28px', flexDirection: isMobile ? 'column' : 'row' }}>
              <div style={{ flexShrink: 0, alignSelf: isMobile ? 'center' : 'flex-start' }}>
                <ShieldArt size={126} />
              </div>
              <ul style={{ flex: 1, minWidth: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '11px' }}>
                {PRIVACY_POINTS.map(point => (
                  <li key={point} style={{ display: 'flex', gap: '9px' }}>
                    <ShieldCheck size={15} color="var(--green)" strokeWidth={2.2} style={{ flexShrink: 0, marginTop: '3px' }} />
                    <span style={{ fontSize: '13px', color: 'var(--gray-600)', lineHeight: 1.65 }}>{point}</span>
                  </li>
                ))}
              </ul>
            </div>
            <p style={{ fontSize: '12.5px', color: 'var(--gray-400)', marginTop: '16px' }}>
              The full detail lives in our <Link to="/privacy" style={{ fontWeight: 600 }}>Privacy Policy</Link> and{' '}
              <Link to="/terms" style={{ fontWeight: 600 }}>Terms of Service</Link>.
            </p>
          </Card>
        </Reveal>

        {/* ── FAQ ── */}
        <SectionHeading eyebrow="Questions" title="Frequently asked" />
        <Reveal>
          <Card style={{ marginBottom: isMobile ? '30px' : '44px' }}>
            {FAQS.map(f => <Faq key={f.q} {...f} />)}
          </Card>
        </Reveal>

        {/* ── CTA ── */}
        <Reveal>
          <div style={{
            background: 'linear-gradient(135deg, var(--red-dark), var(--red))',
            borderRadius: 'var(--radius-lg)',
            padding: isMobile ? '26px 22px' : '36px 40px',
            color: 'white', textAlign: 'center', position: 'relative', overflow: 'hidden',
          }}>
            <div style={{ position: 'absolute', left: '-60px', bottom: '-80px', width: '200px', height: '200px', borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />
            <div style={{ position: 'relative', zIndex: 1 }}>
              <h2 style={{ fontSize: isMobile ? '20px' : '24px', fontWeight: 900, letterSpacing: '-0.03em', marginBottom: '8px' }}>
                That's the whole thing. Ready?
              </h2>
              <p style={{ fontSize: '13.5px', opacity: 0.85, lineHeight: 1.6, maxWidth: '460px', margin: '0 auto 22px' }}>
                It takes about a minute to register, and one accepted request to make a difference to somebody's day.
              </p>
              {/* Plain buttons rather than <Button>: its variant styles are
                  applied after the style prop, so an on-red palette can't be
                  passed in. Home's hero does the same for the same reason. */}
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
                {CTAS.map(({ to, Icon, label, primary }) => (
                  <Link key={label} to={to}>
                    <button style={primary ? ctaPrimary : ctaSecondary}>
                      <Icon size={15} /> {label}
                    </button>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </PageLayout>
  );
}
